import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar, faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const ReferencesDistributionByCarrierBarChart = ({ data, isLoading }) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    if (isLoading || !data || data.length === 0) {
      return;
    }

    const processData = () => {
      const entryPointCounts = {};
      const colors = {};

      data.forEach((item) => {
        const entryPoint = item.header.pedimento.pedimento_entry_point;
        const mexicanCarrier = item.header.detail.traffic_data.mexican_carrier;
        const usCarrier = item.header.detail.traffic_data.us_carrier;

        if (!entryPointCounts[entryPoint]) {
          entryPointCounts[entryPoint] = {};
        }

        if (!entryPointCounts[entryPoint][mexicanCarrier]) {
          entryPointCounts[entryPoint][mexicanCarrier] = 0;
        }
        if (!entryPointCounts[entryPoint][usCarrier]) {
          entryPointCounts[entryPoint][usCarrier] = 0;
        }

        entryPointCounts[entryPoint][mexicanCarrier] += 1;
        entryPointCounts[entryPoint][usCarrier] += 1;

        // Assign random colors to carriers
        if (!colors[mexicanCarrier]) {
          colors[mexicanCarrier] =
            "#" + ((Math.random() * 0xffffff) << 0).toString(16);
        }
        if (!colors[usCarrier]) {
          colors[usCarrier] =
            "#" + ((Math.random() * 0xffffff) << 0).toString(16);
        }
      });

      const entryPoints = Object.keys(entryPointCounts);
      const carriers = Array.from(
        new Set(
          data
            .map((item) => [
              item.header.detail.traffic_data.mexican_carrier,
              item.header.detail.traffic_data.us_carrier,
            ])
            .flat()
        )
      );

      const datasets = carriers.map((carrier) => ({
        label: carrier,
        data: entryPoints.map(
          (entryPoint) => entryPointCounts[entryPoint][carrier] || 0
        ),
        backgroundColor: colors[carrier],
      }));

      setChartData({
        labels: entryPoints,
        datasets: datasets,
      });
    };

    processData();
  }, [data, isLoading]);

  const options = {
    indexAxis: "y", // This makes the bar chart horizontal
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
      },
      y: {
        stacked: true,
      },
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "right",
      },
      datalabels: {
        display: true,
        color: "#fff",
        formatter: (value) => (value > 0 ? value : ""),
      },
    },
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg h-custom-card-big flex flex-col justify-between">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartBar} className="text-blue-600 mr-2" />
          References Distribution by Carrier
        </h2>
      </div>
      <div className="flex-grow relative">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-xl"
            />
            <span className="ml-2 text-lg">Loading...</span>
          </div>
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

export default ReferencesDistributionByCarrierBarChart;
